// studioCreate — what the Studio's Create menu and its "/" at the caret
// make, and where it lands (pen "Studio · Create-meny", "Snedstrecksmeny";
// #317). Pure: the cards on the project's canvas and the page shown are
// projected into
//  - the KINDS one can make: Module, Page, Quiz, Document (never Course:
//    the root is the course; Slides has no kind in the domain yet, so it is
//    not offered); from one source, Page and Quiz (pen Q73);
//  - the SOURCES of the project as the menu lists them (pen Q73): most
//    recently touched first, each with the word for its kind, and the
//    filter over them;
//  - the STEP the menu shows, and the one back (or Escape) goes to;
//  - the LANDING: the container a new part goes into, at its end (the
//    page's own container, the one a document lies in, or the root course),
//    with the part it lands after;
//  - the CHOICES for "Change": the root course and every module under it
//    at any depth, then every other container on the canvas;
// Every case is explicit; a project with no container has no landing
// (Create then makes a new module from a source, or nothing).

export type {
    CreateKind,
    CreateStep,
    FromSourceKind,
    Landing,
    LandingChoice,
    SourceKind,
    SourceRow,
};
export {
    createKinds,
    fromSourceKinds,
    landingChoices,
    landingFor,
    landingOf,
    partsBefore,
    plannableOf,
    shownStep,
    sourceRowsMatching,
    sourceRowsOf,
    stepBack,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { PlannableKind } from "../../app/ports/brief.js";
import type { Element } from "../Editor/types.js";
import type {
    ContainerCard,
    ContainerPart,
    StudioCard,
} from "../Studio/studioCards.js";
import { type DeskState, deskBinders } from "../Studio/studioDesk.js";
import { rootOf } from "./studioPages.js";

type CreateKind = "module" | "page" | "quiz" | "document";

// In the order the menus list them.
const createKinds: readonly CreateKind[] = [
    "module",
    "page",
    "quiz",
    "document",
];

// What one source is made into from the menu: the one create command makes
// a document from sources, never a module (a module from a source is asked
// for with Ask here).
type FromSourceKind = Extract<CreateKind, "page" | "quiz">;
const fromSourceKinds: readonly FromSourceKind[] = ["page", "quiz"];

// A document kind as the domain plans it (a module is made as a container).
const plannableOf = (kind: Exclude<CreateKind, "module">): PlannableKind => {
    switch (kind) {
        case "page":
            return "page";
        case "quiz":
            return "quiz";
        case "document":
            return "handout";
        default:
            return exhaustSwitchCase(kind);
    }
};

// A container a new part can land in.
type LandingChoice = {
    readonly id: string;
    readonly title: string;
    // How deep it lies (the root course: 0), for the Change list's indent.
    readonly depth: number;
};

type Landing = LandingChoice & {
    // The part it lands after (absent: the container is empty).
    readonly after: string | undefined;
};

// A container's own parts that are material (a source it lists is never
// a part: LM-1).
const materialParts = (parts: readonly ContainerPart[]) =>
    parts.filter((p) => p.kind !== "source");

const landingFrom = (
    id: string,
    title: string,
    depth: number,
    parts: readonly ContainerPart[],
): Landing => {
    const own = materialParts(parts);
    return {
        id,
        title,
        depth,
        after: own.at(-1)?.title,
    };
};

// A module inside a container, found at any depth (first in reading order).
const findModule = (
    parts: readonly ContainerPart[],
    id: string,
    depth: number,
): Landing | undefined =>
    parts
        .filter((p) => p.kind === "module")
        .reduce<Landing | undefined>(
            (found, p) =>
                found ??
                (p.id === id
                    ? landingFrom(p.id, p.title, depth, p.parts)
                    : findModule(p.parts, id, depth + 1)),
            undefined,
        );

const containers = (cards: readonly StudioCard[]): readonly ContainerCard[] =>
    cards.filter((c): c is ContainerCard => c.type === "container");

// A container by id: a card on the canvas, or a module inside one.
const landingFor = (
    cards: readonly StudioCard[],
    id: string,
): Landing | undefined =>
    containers(cards).reduce<Landing | undefined>(
        (found, card) =>
            found ??
            (card.id === id
                ? landingFrom(card.id, card.title, 0, card.parts)
                : findModule(card.parts, id, 1)),
        undefined,
    );

// Where a new part lands from the page shown: the container shown, the one
// the document shown lies in, else the root course, else the first
// container on the canvas, else nowhere.
const landingOf = (
    cards: readonly StudioCard[],
    desk: DeskState,
): Landing | undefined => {
    const binders = deskBinders(desk);
    const shown = binders.at(-1);
    const fromDesk =
        shown === undefined ? undefined : landingFor(cards, shown.id);
    if (fromDesk !== undefined) return fromDesk;
    // A document opened loose (no binder above it): the root.
    const root = rootOf(cards);
    if (root.type === "course")
        return landingFrom(root.card.id, root.card.title, 0, root.card.parts);
    const first = containers(cards)[0];
    return first === undefined
        ? undefined
        : landingFrom(first.id, first.title, 0, first.parts);
};

// Every container a part can land in: each container card, then its
// modules at any depth, in reading order.
const landingChoices = (
    cards: readonly StudioCard[],
): readonly LandingChoice[] => {
    const modules = (
        parts: readonly ContainerPart[],
        depth: number,
    ): readonly LandingChoice[] =>
        parts.flatMap((p) =>
            p.kind === "module"
                ? [
                      { id: p.id, title: p.title, depth },
                      ...modules(p.parts, depth + 1),
                  ]
                : [],
        );
    return containers(cards).flatMap((c) => [
        { id: c.id, title: c.title, depth: 0 },
        ...modules(c.parts, 1),
    ]);
};

// Where "/" typed in the block `anchor` of a course or module page lands a
// new part: after the parts above the caret (their count is the part's
// index; undefined: no such block, so the end).
const partsBefore = (
    elements: readonly Element[],
    anchor: string,
): number | undefined => {
    const at = elements.findIndex((e) => e.id === anchor);
    return at < 0
        ? undefined
        : elements.slice(0, at).filter((e) => e.type === "part").length;
};

// === The steps (pen Q73) ===

// The step the Create menu shows. View state: it goes when the menu closes.
//  - start: the one Create menu (Ask here, Create from a source…, Insert);
//  - sources: the project's sources, with a filter (step 1);
//  - adding: as `sources`, while the add flow runs; a source that was not
//    among `known` when it started is picked once it appears;
//  - make: what to make from `source` (step 2);
//  - place: where it lands (Change).
type CreateStep =
    | { readonly type: "start" }
    | { readonly type: "sources" }
    | { readonly type: "adding"; readonly known: readonly string[] }
    | { readonly type: "make"; readonly source: string }
    | { readonly type: "place" };

// The step shown: a source added while `adding` is the one picked.
const shownStep = (
    step: CreateStep,
    sources: readonly string[],
): CreateStep => {
    if (step.type !== "adding") return step;
    const added = sources.find((id) => !step.known.includes(id));
    return added === undefined ? step : { type: "make", source: added };
};

// Back one step (the head's chevron, Escape); null: the menu closes. At the
// caret the menu opens on the sources, so back from there closes it (the
// "/" menu before it is the editor's own).
const stepBack = (
    step: CreateStep,
    opening: "button" | "caret",
): CreateStep | null => {
    switch (step.type) {
        case "start":
            return null;
        case "sources":
        case "adding":
            return opening === "caret" ? null : { type: "start" };
        case "make":
            return { type: "sources" };
        case "place":
            return { type: "start" };
        default:
            return exhaustSwitchCase(step);
    }
};

// === The sources (pen Q73, step 1) ===

// What a source is, by its format tag: the word its row says under its name.
type SourceKind =
    | { readonly type: "pdf" }
    | { readonly type: "slides" }
    | { readonly type: "document" }
    | { readonly type: "image" }
    // A format the menu has no word for: its tag as it is ("EPUB").
    | { readonly type: "other"; readonly tag: string }
    // No format known (the title names none).
    | { readonly type: "none" };

type SourceRow = {
    readonly id: string;
    readonly title: string;
    readonly kind: SourceKind;
};

const kindOfTag = (tag: string): SourceKind => {
    const t = tag.trim().toUpperCase();
    switch (t) {
        case "":
            return { type: "none" };
        case "PDF":
            return { type: "pdf" };
        case "PPTX":
        case "PPT":
        case "KEY":
        case "ODP":
            return { type: "slides" };
        case "DOCX":
        case "DOC":
        case "ODT":
        case "RTF":
        case "TXT":
        case "MD":
            return { type: "document" };
        case "PNG":
        case "JPG":
        case "JPEG":
        case "GIF":
        case "WEBP":
            return { type: "image" };
        default:
            return { type: "other", tag: t };
    }
};

// The project's sources (its source cards), most recently touched first by
// the library's facts (`touched`: id to ISO time; one with no time keeps
// its place after those with one). The app does not track "last used";
// the last edit, else the upload, is the nearest fact.
const sourceRowsOf = (
    cards: readonly StudioCard[],
    touched: ReadonlyMap<string, string>,
): readonly SourceRow[] =>
    cards
        .flatMap((c, at) =>
            c.type === "source"
                ? [
                      {
                          at,
                          when: touched.get(c.id) ?? "",
                          row: {
                              id: c.id,
                              title: c.title,
                              kind: kindOfTag(c.format),
                          },
                      },
                  ]
                : [],
        )
        .sort((a, b) =>
            a.when === b.when ? a.at - b.at : a.when < b.when ? 1 : -1,
        )
        .map((s) => s.row);

// The rows whose name holds every word typed (case-insensitive); nothing
// typed keeps every row. Cost: O(rows · name length).
const sourceRowsMatching = (
    rows: readonly SourceRow[],
    query: string,
): readonly SourceRow[] => {
    const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((r) => {
        const name = r.title.toLocaleLowerCase();
        return words.every((w) => name.includes(w));
    });
};
